import React, { CSSProperties, PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { connectRedux } from '../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../helpers/withRouter'
import { Button, Modal } from '../../components'
import { appsLogosSelected } from '../../helpers/app'
import { images } from '../../images'
import { LiteralsService } from '../../services'
import {
  clearSignInFieldsAction,
  clearSignUpFieldsAction,
  clearVerificationFieldsAction,
  hideGuestSignUpAction,
  recoverPasswordFailedAction,
  resetRecoverPasswordFieldsAction,
  setGuestSignUpScreenAction
} from '../../store/actions'
import { IStoreState } from '../../store/states'
import { GuestSignUpScreen } from '../../store/types/user-info'
import GoogleSignInButton, { isGoogleSignInAvailable } from '../GoogleSignInButton'
import NewPasswordRequiredForm from '../NewPasswordRequiredForm'
import { RecoverPasswordRequestCodeForm, RecoverPasswordRestoreForm } from '../RecoverPasswordForms'
import SignInForm from '../SignInForm'
import { SignUpStep1Form, SignUpStep2Form, SignUpStep3Form, SIGN_UP_STEP_TITLES } from '../SignUpForms'
import { VerificationResendForm, VerificationStepForm } from '../VerificationForms'
import StyledDiv from './styles'

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  isVisible: boolean
  screen: GuestSignUpScreen
}

// The shared Modal frame comes squared and padded; this one is a rounded card with its own padding.
// The frame must not clip (overflow + border-radius): Chromium then stops routing clicks to
// cross-origin iframes inside it, and on https Google's sign in button is one. The card itself
// scrolls instead (see styles).
const FRAME_STYLE: CSSProperties = { borderRadius: 28, padding: 0, maxHeight: 'none', overflow: 'visible' }

const SIGN_UP_SCREENS: GuestSignUpScreen[] = ['signUp1', 'signUp2', 'signUp3']

// Title of every screen that has one (the choice screen and the login carry the lock instead),
// and the screen the back arrow returns to.
const TITLES: Partial<Record<GuestSignUpScreen, string>> = {
  // The prompt's first step is titled by what it does ("create your account"), not by its data.
  signUp1: 'createYourAccount',
  signUp2: SIGN_UP_STEP_TITLES[1],
  signUp3: SIGN_UP_STEP_TITLES[2],
  recoverRequest: 'recoverYourPassword',
  recoverRestore: 'reestablishPassword',
  verification: 'verifyYourEmail',
  verificationResend: 'requestNewSignUpVerificationCode',
  newPassword: 'newPassword'
}

const BACK_TO: Partial<Record<GuestSignUpScreen, GuestSignUpScreen>> = {
  signIn: 'intro',
  signUp1: 'intro',
  signUp2: 'signUp1',
  signUp3: 'signUp2',
  recoverRequest: 'signIn',
  recoverRestore: 'recoverRequest',
  verification: 'signIn',
  verificationResend: 'verification',
  newPassword: 'signIn'
}

/**
 * Shown to a guest who tries something that needs an account — picking a stop, opening another
 * section. Opens on a choice (Google, sign in, create an account, stay a guest) and then hosts,
 * without leaving the map, the same flows as the pages: sign in, the three sign up steps, recover
 * password and account verification. Which screen shows is Redux state, so the sagas that move
 * between those screens on the pages (see sagas/auth-navigation) move the prompt the same way.
 */
class GuestSignUpModal extends PureComponent<IProps> {
  public componentDidUpdate(prevProps: IProps): void {
    const { isVisible, location } = this.props

    // Anything that leaves for another page (a route the prompt does not host) closes it.
    if (isVisible && location.pathname !== prevProps.location.pathname) this.close()
  }

  // Cross and chevron drawn here at the mock's weight; the shared ic-close asset is heavier.
  private get closeIcon(): ReactNode {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    )
  }

  private get backIcon(): ReactNode {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path d="M10 2L4 8l6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }

  // Material "account circle", in the brand colour, above the sign up steps' titles.
  private get avatarIcon(): ReactNode {
    return (
      <svg className="avatar" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
      </svg>
    )
  }

  // The sign up's progress: one numbered dot per step, joined by a line, the current one filled.
  private stepper(current: number): ReactNode {
    return (
      <ol className="steps" aria-label={ `${ current + 1 }/${ SIGN_UP_SCREENS.length }` }>
        { SIGN_UP_SCREENS.map((screen, index) => (
          <li key={ screen } className={ index === current ? 'current' : '' }>{ index + 1 }</li>
        )) }
      </ol>
    )
  }

  private get lockAndTitle(): ReactNode {
    return (
      <div className="intro">
        <img className="lock" src={ images.lockRequired } alt="" />
        <h3>{ LiteralsService.get('registrationRequired') }</h3>
        <p>{ LiteralsService.get('guestSignUpMessage') }</p>
      </div>
    )
  }

  // The login in the prompt is the brand's, like the page: the logo and "sign in" above the form.
  private get brandAndTitle(): ReactNode {
    return (
      <div className="brand">
        <img className={ appsLogosSelected?.classRounded } src={ appsLogosSelected?.logoRounded } alt={ appsLogosSelected?.alt } />
        <h3>{ LiteralsService.get('signIn', true) }</h3>
      </div>
    )
  }

  private get intro(): ReactNode {
    return (
      <>
        { this.lockAndTitle }
        <section className="choices">
          { isGoogleSignInAvailable && <GoogleSignInButton /> }
          <Button
            title={ LiteralsService.get('signIn') }
            onPress={ this.showSignIn }
            filled
            upper
          />
          <div className="divider">{ LiteralsService.get('noAccountYet') }</div>
          <Button
            title={ LiteralsService.get('createAccount') }
            onPress={ this.startSignUp }
            upper
          />
          <div className="divider">{ LiteralsService.get('orElse') }</div>
          <span className="stay-guest" onClick={ this.close }>
            { LiteralsService.get('guestMode').toUpperCase() }
          </span>
        </section>
      </>
    )
  }

  private get body(): ReactNode {
    switch (this.props.screen) {
      case 'intro':
        return this.intro
      // The same form as /sign-in: signing in ends guest mode, which closes the prompt by itself.
      case 'signIn':
        return (
          <>
            { this.brandAndTitle }
            <SignInForm
              onForgotPassword={ this.startRecoverPassword }
              onVerification={ this.startVerification }
              onSignUp={ this.startSignUp }
              onGuest={ this.close }
            />
          </>
        )
      case 'signUp1': return <SignUpStep1Form onContinue={ () => this.show('signUp2') } onSignIn={ this.showSignIn } />
      case 'signUp2': return <SignUpStep2Form onContinue={ () => this.show('signUp3') } />
      case 'signUp3': return <SignUpStep3Form />
      case 'recoverRequest': return <RecoverPasswordRequestCodeForm />
      case 'recoverRestore': return <RecoverPasswordRestoreForm />
      case 'verification': return <VerificationStepForm onResend={ () => this.show('verificationResend') } />
      case 'verificationResend': return <VerificationResendForm />
      case 'newPassword': return <NewPasswordRequiredForm />
    }
  }

  public render(): ReactNode {
    const { isVisible, screen } = this.props
    const title = TITLES[screen]
    const signUpIndex = SIGN_UP_SCREENS.indexOf(screen)

    // Rendered hidden rather than not at all: that is how Modal gives the page its scroll back.
    return (
      <Modal isHidden={ !isVisible } onOverlayClick={ this.close } style={ FRAME_STYLE }>
        { isVisible && <StyledDiv>
          { /* Back and close sit in the two top corners, same size, same grey. */ }
          { BACK_TO[screen] &&
            <button type="button" className="corner back" onClick={ this.back } aria-label={ LiteralsService.get('back', true) }>
              { this.backIcon }
            </button>
          }
          <button type="button" className="corner close" onClick={ this.close } aria-label={ LiteralsService.get('close', true) }>
            { this.closeIcon }
          </button>
          { /* The steps sit on the corner buttons' row, between them. */ }
          { signUpIndex >= 0 && this.stepper(signUpIndex) }
          <form onSubmit={ e => e.preventDefault() }>
            { title &&
              <header className={ signUpIndex >= 0 ? 'sign-up' : '' }>
                <div className="titles">
                  { signUpIndex >= 0 && this.avatarIcon }
                  <h3>{ LiteralsService.get(title, true) }</h3>
                </div>
              </header>
            }
            { this.body }
          </form>
        </StyledDiv> }
      </Modal>
    )
  }

  private show = (screen: GuestSignUpScreen): void => {
    this.props.dispatch(setGuestSignUpScreenAction(screen))
  }

  private back = (): void => {
    const { screen, dispatch } = this.props
    // A stale "invalid code" must not greet the user next time.
    if (screen === 'recoverRestore') dispatch(recoverPasswordFailedAction(''))
    this.show(BACK_TO[screen] ?? 'intro')
  }

  private showSignIn = (): void => {
    this.props.dispatch(clearSignInFieldsAction())
    this.show('signIn')
  }

  private startSignUp = (): void => {
    this.props.dispatch(clearSignUpFieldsAction())
    this.show('signUp1')
  }

  private startRecoverPassword = (): void => {
    this.props.dispatch(resetRecoverPasswordFieldsAction())
    this.show('recoverRequest')
  }

  private startVerification = (): void => {
    this.props.dispatch(clearVerificationFieldsAction())
    this.show('verification')
  }

  private close = (): void => {
    this.props.dispatch(hideGuestSignUpAction())
  }
}

const mapStateToProps = ({ userInfo }: IStoreState) => ({
  isVisible: userInfo.isGuest && userInfo.showGuestSignUp,
  screen: userInfo.guestSignUpScreen
})

export default withRouter(connectRedux(mapStateToProps, GuestSignUpModal))
